import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { ConnectionStatus, SessionInfo } from '@tcg/shared';
import { api } from './api';

interface SessionContextValue {
  loading: boolean;
  user: SessionInfo['user'];
  connections: ConnectionStatus[];
  refresh: () => Promise<void>;
  login: (email: string, password: string) => Promise<void>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>;
  logout: () => Promise<void>;
}

const SessionContext = createContext<SessionContextValue | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [loading, setLoading] = useState(true);
  const [session, setSession] = useState<SessionInfo>({ user: null, connections: [] });

  const refresh = useCallback(async () => {
    const info = await api.me();
    setSession(info);
  }, []);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const info = await api.me();
        if (active) setSession(info);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const info = await api.login(email, password);
    setSession(info);
  }, []);

  const changePassword = useCallback(async (currentPassword: string, newPassword: string) => {
    const info = await api.changePassword(currentPassword, newPassword);
    setSession(info);
  }, []);

  const logout = useCallback(async () => {
    await api.logout();
    setSession({ user: null, connections: [] });
  }, []);

  const value = useMemo<SessionContextValue>(
    () => ({
      loading,
      user: session.user,
      connections: session.connections,
      refresh,
      login,
      changePassword,
      logout,
    }),
    [loading, session, refresh, login, changePassword, logout],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionContextValue {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error('useSession must be used within SessionProvider');
  return ctx;
}
